import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// 創建axios實例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 請求攔截器 - 添加認證token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 響應攔截器 - 處理錯誤
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 軌道車數據接口
export interface TrackVehicle {
  id: string;
  name: string;
  status: 'active' | 'idle' | 'charging' | 'maintenance' | 'emergency';
  position: number;
  batteryLevel: number;
  temperature: number;
  speed: number;
  lastUpdate: Date;
  thermalCamera: {
    status: 'online' | 'offline';
    temperature: number;
    resolution: string;
    frameRate: number;
  };
  config: {
    patrolMode: 'auto' | 'manual';
    speed: number;
    thermalSensitivity: 'low' | 'medium' | 'high';
    alertThreshold: number;
  };
}

// 熱成像警報接口
export interface ThermalAlert {
  id: string;
  vehicleId: string;
  location: string;
  temperature: number;
  alertLevel: 'warning' | 'critical' | 'emergency';
  timestamp: Date;
  status: 'active' | 'investigating' | 'resolved' | 'fire_alert_triggered';
  description: string;
  detectedBy: string;
  fireAlertTime?: Date;
  lastUpdate?: Date;
}

// 系統狀態接口
export interface SystemStatus {
  isActive: boolean;
  autoPatrol: boolean;
  emergencyMode: boolean;
  fireSuppressionReady: boolean;
  lastUpdate: Date;
  vehicleCount: number;
  activeVehicles: number;
  activeAlerts: number;
}

// 熱成像數據接口
export interface ThermalData {
  timestamp: Date;
  temperature: number;
  vehicleCount: number;
  alertCount: number;
  vehicleId: string;
}

// 巡邏統計接口
export interface PatrolStatistics {
  totalPatrolTime: number;
  totalDistance: number;
  vehicleUtilization: number;
  anomaliesDetected: number;
  alertsGenerated: number;
  falsePositives: number;
  responseTime: number;
  batteryEfficiency: number;
}

// 異常統計接口
export interface AnomalyStatistics {
  totalAnomalies: number;
  byLevel: {
    warning: number;
    critical: number;
    emergency: number;
  };
  byStatus: {
    active: number;
    resolved: number;
    investigating: number;
  };
}

// 消防安全狀態接口
export interface FireSafetyStatus {
  systemStatus: {
    operational: boolean;
    systemMode: 'normal' | 'alert' | 'emergency' | 'maintenance';
    suppressionActive: boolean;
    evacuationInProgress: boolean;
    fireServiceNotified: boolean;
    activeAlertsCount: number;
    healthPercentage: number;
    lastMaintenance: string;
  };
  activeAlerts: FireAlert[];
  deviceStatus: {
    detectors: FireDetector[];
    sprinklers: SprinklerDevice[];
    foamSystems: FoamDevice[];
    gasSystems: GasDevice[];
  };
  statistics: {
    totalDetectors: number;
    activeAlerts: number;
    suppressionDevices: number;
    systemHealth: number;
  };
}

export interface FireAlert {
  id: string;
  type: 'fire' | 'manual_fire' | 'warning';
  severity: 'low' | 'medium' | 'high' | 'critical';
  detector?: string;
  zone: number;
  position: number;
  risks?: Array<{
    type: 'temperature' | 'smoke' | 'flame' | 'gas';
    level: number;
    value: number;
  }>;
  status: 'active' | 'investigating' | 'resolved';
  createdAt: string;
  confirmedAt?: string;
  resolvedAt?: string;
  reason?: string;
  actions: Array<{
    action: string;
    timestamp: string;
    details: string;
  }>;
}

export interface FireDetector {
  id: string;
  type: 'multi-sensor' | 'smoke' | 'heat' | 'flame' | 'gas';
  zone: number;
  position: number;
  status: 'normal' | 'warning' | 'alarm' | 'fault';
  temperature: number;
  smoke: number;
  flame: number;
  gas: number;
  batteryLevel: number;
  lastTest: string;
}

export interface SprinklerDevice {
  id: string;
  zone: number;
  status: 'standby' | 'active' | 'testing' | 'fault';
  pressure: number;
  flowRate: number;
  valvePosition: 'open' | 'closed';
  lastMaintenance: string;
}

export interface FoamDevice {
  id: string;
  status: 'standby' | 'active' | 'empty' | 'fault';
  level: number;
  pressure: number;
  concentration: number;
  lastRefill: string;
}

export interface GasDevice {
  id: string;
  zone: number;
  status: 'standby' | 'discharging' | 'empty' | 'fault';
  pressure: number;
  concentration: number;
  dischargeValve: 'open' | 'closed';
  lastInspection: string;
}

export interface FireEvent {
  id: string;
  alertId: string;
  startTime: string;
  endTime?: string;
  zone: number;
  position: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  cause: string;
  damage: string;
  responseTime: number;
  suppressionMethods: string[];
  evacuationStatus: string;
  casualties: number;
  status: 'active' | 'contained' | 'extinguished' | 'under_investigation';
}

class CircularTrackService {
  private baseURL: string;

  constructor() {
    this.baseURL = `/circular-track`;
  }

  // ==================== 軌道車管理 ====================

  /**
   * 獲取所有軌道車
   */
  async getTrackVehicles(): Promise<TrackVehicle[]> {
    try {
      const response = await api.get(`${this.baseURL}/vehicles`);
      return response.data.data;
    } catch (error) {
      console.error('獲取軌道車數據失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取特定軌道車詳情
   */
  async getTrackVehicleById(vehicleId: string): Promise<TrackVehicle> {
    try {
      const response = await api.get(`${this.baseURL}/vehicles/${vehicleId}`);
      return response.data.data;
    } catch (error) {
      console.error('獲取軌道車詳情失敗:', error);
      throw error;
    }
  }

  /**
   * 控制軌道車
   */
  async controlTrackVehicle(
    vehicleId: string, 
    action: 'start' | 'stop' | 'charge' | 'maintenance' | 'emergency',
    parameters?: any
  ): Promise<TrackVehicle> {
    try {
      const response = await api.post(`${this.baseURL}/vehicles/${vehicleId}/control`, {
        action,
        parameters
      });
      return response.data.data;
    } catch (error) {
      console.error('控制軌道車失敗:', error);
      throw error;
    }
  }

  /**
   * 更新軌道車配置
   */
  async updateVehicleConfig(vehicleId: string, config: Partial<TrackVehicle['config']>): Promise<TrackVehicle> {
    try {
      const response = await api.put(`${this.baseURL}/vehicles/${vehicleId}/config`, config);
      return response.data.data;
    } catch (error) {
      console.error('更新軌道車配置失敗:', error);
      throw error;
    }
  }

  // ==================== 熱成像監控 ====================

  /**
   * 獲取熱成像數據
   */
  async getThermalData(params?: {
    vehicleId?: string;
    startTime?: string;
    endTime?: string;
  }): Promise<ThermalData[]> {
    try {
      const response = await api.get(`${this.baseURL}/thermal/data`, { params });
      return response.data.data;
    } catch (error) {
      console.error('獲取熱成像數據失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取熱成像警報
   */
  async getThermalAlerts(params?: {
    page?: number;
    limit?: number;
    status?: string;
    alertLevel?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    data: ThermalAlert[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  }> {
    try {
      const response = await api.get(`${this.baseURL}/thermal/alerts`, { params });
      return response.data;
    } catch (error) {
      console.error('獲取熱成像警報失敗:', error);
      throw error;
    }
  }

  /**
   * 創建熱成像警報
   */
  async createThermalAlert(alertData: Partial<ThermalAlert>): Promise<ThermalAlert> {
    try {
      const response = await api.post(`${this.baseURL}/thermal/alerts`, alertData);
      return response.data.data;
    } catch (error) {
      console.error('創建熱成像警報失敗:', error);
      throw error;
    }
  }

  /**
   * 更新熱成像警報
   */
  async updateThermalAlert(alertId: string, updateData: Partial<ThermalAlert>): Promise<ThermalAlert> {
    try {
      const response = await api.put(`${this.baseURL}/thermal/alerts/${alertId}`, updateData);
      return response.data.data;
    } catch (error) {
      console.error('更新熱成像警報失敗:', error);
      throw error;
    }
  }

  // ==================== 系統控制 ====================

  /**
   * 獲取系統狀態
   */
  async getSystemStatus(): Promise<SystemStatus> {
    try {
      const response = await api.get(`${this.baseURL}/system/status`);
      return response.data.data;
    } catch (error) {
      console.error('獲取系統狀態失敗:', error);
      throw error;
    }
  }

  /**
   * 控制自動巡邏
   */
  async controlAutoPatrol(action: 'start' | 'stop'): Promise<SystemStatus> {
    try {
      const response = await api.post(`${this.baseURL}/system/patrol/${action}`);
      return response.data.data;
    } catch (error) {
      console.error('控制自動巡邏失敗:', error);
      throw error;
    }
  }

  /**
   * 緊急停止
   */
  async emergencyStop(): Promise<SystemStatus> {
    try {
      const response = await api.post(`${this.baseURL}/system/emergency-stop`);
      return response.data.data;
    } catch (error) {
      console.error('緊急停止失敗:', error);
      throw error;
    }
  }

  // ==================== 消防聯動 ====================

  /**
   * 獲取消防系統狀態
   */
  async getFireSafetyStatus(): Promise<FireSafetyStatus> {
    try {
      const response = await api.get(`${this.baseURL}/fire-safety/status`);
      return response.data.data;
    } catch (error) {
      console.error('獲取消防安全狀態失敗:', error);
      throw error;
    }
  }

  /**
   * 控制消防系統
   */
  async controlFireSystem(action: string, options: {
    deviceType?: string;
    deviceId?: string;
    zone?: number;
    parameters?: any;
  } = {}): Promise<any> {
    try {
      const response = await api.post(`${this.baseURL}/fire-safety/control`, {
        action,
        ...options
      });
      return response.data;
    } catch (error) {
      console.error('控制消防系統失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取消防事件
   */
  async getFireEvents(limit: number = 10): Promise<FireEvent[]> {
    try {
      const response = await api.get(`${this.baseURL}/fire-safety/events?limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('獲取消防事件失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取消防設備詳情
   */
  async getFireDeviceDetails(deviceType: string, deviceId: string): Promise<any> {
    try {
      const response = await api.get(`${this.baseURL}/fire-safety/devices/${deviceType}/${deviceId}`);
      return response.data.data;
    } catch (error) {
      console.error('獲取消防設備詳情失敗:', error);
      throw error;
    }
  }

  /**
   * 觸發手動消防警報
   */
  async triggerManualFireAlarm(zone: number, reason: string): Promise<FireAlert> {
    return this.controlFireSystem('manual_alarm', {
      zone,
      parameters: { reason }
    });
  }

  /**
   * 觸發消防警報
   */
  async triggerFireAlert(alertId: string): Promise<ThermalAlert> {
    try {
      const response = await api.post(`${this.baseURL}/fire-safety/alert/${alertId}`);
      return response.data.data;
    } catch (error) {
      console.error('觸發火警失敗:', error);
      throw error;
    }
  }

  /**
   * 解決消防警報
   */
  async resolveFireAlert(alertId: string): Promise<void> {
    try {
      await api.post(`${this.baseURL}/fire-safety/resolve/${alertId}`);
    } catch (error) {
      console.error('解決火警失敗:', error);
      throw error;
    }
  }

  /**
   * 測試消防設備
   */
  async testFireDevice(deviceType: string, deviceId: string): Promise<any> {
    try {
      const response = await api.post(`${this.baseURL}/fire-safety/test/${deviceType}/${deviceId}`);
      return response.data.data;
    } catch (error) {
      console.error('測試消防設備失敗:', error);
      throw error;
    }
  }

  /**
   * 重置消防系統
   */
  async resetFireSystem(): Promise<void> {
    try {
      await api.post(`${this.baseURL}/fire-safety/reset`);
    } catch (error) {
      console.error('重置消防系統失敗:', error);
      throw error;
    }
  }

  /**
   * 啟動消防系統
   */
  async activateFireSuppression(location: string, suppressionType?: string): Promise<any> {
    try {
      const response = await api.post(`${this.baseURL}/fire-safety/suppression/${location}`, {
        suppressionType: suppressionType || 'auto'
      });
      return response.data.data;
    } catch (error) {
      console.error('啟動滅火系統失敗:', error);
      throw error;
    }
  }

  // ==================== 統計和報表 ====================

  /**
   * 獲取巡邏統計
   */
  async getPatrolStatistics(period?: string): Promise<PatrolStatistics> {
    try {
      const response = await api.get(`${this.baseURL}/statistics/patrol`, {
        params: period ? { period } : {}
      });
      return response.data.data;
    } catch (error) {
      console.error('獲取巡邏統計失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取異常統計
   */
  async getAnomalyStatistics(): Promise<AnomalyStatistics> {
    try {
      const response = await api.get(`${this.baseURL}/statistics/anomalies`);
      return response.data.data;
    } catch (error) {
      console.error('獲取異常統計失敗:', error);
      throw error;
    }
  }

  // ==================== WebSocket 連接 ====================

  /**
   * 建立 WebSocket 連接以接收實時更新
   */
  connectWebSocket(callbacks: {
    onVehicleUpdate?: (data: any) => void;
    onThermalAlert?: (data: any) => void;
    onFireAlert?: (data: any) => void;
    onEmergencyStop?: (data: any) => void;
  }): WebSocket | null {
    try {
      const socketBase = (process.env.REACT_APP_SOCKET_URL || window.location.origin).replace(/^http/, 'ws');
      const wsUrl = `${socketBase}/ws/track`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('環形軌道 WebSocket 連接已建立');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'vehicleUpdate':
              callbacks.onVehicleUpdate?.(data.payload);
              break;
            case 'thermalAlert':
              callbacks.onThermalAlert?.(data.payload);
              break;
            case 'fireAlert':
              callbacks.onFireAlert?.(data.payload);
              break;
            case 'emergencyStop':
              callbacks.onEmergencyStop?.(data.payload);
              break;
            default:
              console.log('未知的 WebSocket 消息類型:', data.type);
          }
        } catch (error) {
          console.error('解析 WebSocket 消息失敗:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket 錯誤:', error);
      };

      ws.onclose = () => {
        console.log('環形軌道 WebSocket 連接已關閉');
      };

      return ws;
    } catch (error) {
      console.error('建立 WebSocket 連接失敗:', error);
      return null;
    }
  }
}

// 創建並導出服務實例
const circularTrackService = new CircularTrackService();
export default circularTrackService;